// Sub‑reducer for the `visitScheduleStep` slice (normalized).
import {
  ACTION_TYPES,
  getAsyncActionTypeFulfiled,
  getAsyncActionTypePending,
  getAsyncActionTypeReject
} from 'app/redux/features/pad-initialization/actions/padInitialization.types';
import STATUS from 'app/redux/features/pad-initialization/constants/status.constant.js';
import { createEvent } from './calendarVisitSchedule.schema.js';
import { computeUpdateSchedulePrescriptionState } from 'app/redux/features/pad-initialization/reducer/visit-schedule-step/visitScheduleStep.transform';
import { normalizeVisitPrescriptionSchedule } from 'app/redux/features/pad-initialization/reducer/visit-schedule-step/utils/normalizeVisitPrescriptionSchedule';
// utils
import { transformPadVisitToVisitPrescriptionScheduleCooperative } from './utils/transformPadVisitToVisitPrescriptionScheduleCooperative.js';
import { transformPadVisitToCalendarVisitScheduleCooperative } from './utils/transformPadVisitToCalendarVisitScheduleCooperative.js';
import { normalizeVisitPrescriptionScheduleCooperative } from './utils/normalizeVisitPrescriptionScheduleCooperative.js';
import { normalizeCalendarVisitScheduleCooperative } from './utils/normalizeCalendarVisitScheduleCooperative.js';
import { normalizeCalendarVisitSchedule } from './utils/normalizeCalendarVisitSchedule.js';
import { filterAndTransformPadVisitsToCalendarVisitSchedule } from './utils/filterAndTransformPadVisitsToCalendarVisitSchedule.js';
import { filterAndTransformPadVisitsToCalendarVisitScheduleCooperative } from './utils/filterAndTransformPadVisitsToCalendarVisitScheduleCooperative.js';
import { computeUpdateSchedulePrescriptionCooperativeState } from './visitScheduleStep.transform.js';

export const VISIT_SCHEDULE_KEYS = {
  CALENDAR: 'calendarVisitSchedule',
  CALENDAR_COOPERATIVE: 'calendarVisitScheduleCooperative',
  DAILY: 'calendarDailySchedules',
  PRESCRIPTION: 'visitPrescriptionSchedules',
  PRESCRIPTION_COOPERATIVE: 'visitPrescriptionSchedulesCooperative'
};
export const NORMALIZED_KEYS = {
  IDS: 'ids',
  BY_PAD_VISIT_ID: 'byPadVisitId'
};

const initialState = () => ({
  [VISIT_SCHEDULE_KEYS.CALENDAR]: {
    [NORMALIZED_KEYS.IDS]: [],
    [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {},
    status: STATUS.IDLE,
    error: null
  },
  [VISIT_SCHEDULE_KEYS.CALENDAR_COOPERATIVE]: {
    [NORMALIZED_KEYS.IDS]: [],
    [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {},
    status: STATUS.IDLE,
    error: null
  },
  [VISIT_SCHEDULE_KEYS.DAILY]: {
    [NORMALIZED_KEYS.IDS]: [],
    [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {},
    status: STATUS.IDLE,
    error: null
  },
  [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: {
    [NORMALIZED_KEYS.IDS]: [],
    [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {},
    status: STATUS.IDLE,
    error: null
  },
  [VISIT_SCHEDULE_KEYS.PRESCRIPTION_COOPERATIVE]: {
    [NORMALIZED_KEYS.IDS]: [],
    [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {},
    status: STATUS.IDLE,
    error: null
  }
});

const reducer = (state = initialState(), action) => {
  switch (action.type) {
    case ACTION_TYPES.SELECT_AND_UPDATE_DATA: {
      const { events, padVisit } = action.payload;
      const padVisitId = padVisit.id;

      const currentSchedule =
        state[VISIT_SCHEDULE_KEYS.CALENDAR][NORMALIZED_KEYS.BY_PAD_VISIT_ID][padVisitId];
      const nEvents = events.map(event => createEvent(event));

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.CALENDAR]: {
          ...state[VISIT_SCHEDULE_KEYS.CALENDAR],
          [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {
            ...state[VISIT_SCHEDULE_KEYS.CALENDAR][NORMALIZED_KEYS.BY_PAD_VISIT_ID],
            [padVisitId]: {
              ...currentSchedule,
              events: [...nEvents]
            }
          }
        }
      };
    }
    case ACTION_TYPES.APPLY_SELECTION_VISIT_PRESCRIPTION_SCHEDULE: {
      const { events, padVisit } = action.payload;
      const padVisitId = padVisit.id;

      const currentSchedule =
        state[VISIT_SCHEDULE_KEYS.PRESCRIPTION][NORMALIZED_KEYS.BY_PAD_VISIT_ID][padVisitId];

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: {
          ...state[VISIT_SCHEDULE_KEYS.PRESCRIPTION],
          [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {
            ...state[VISIT_SCHEDULE_KEYS.PRESCRIPTION][NORMALIZED_KEYS.BY_PAD_VISIT_ID],
            [padVisitId]: {
              ...currentSchedule,
              events: [...events]
            }
          }
        }
      };
    }
    case ACTION_TYPES.AUTOMATIC_REPETITION: {
      const padVisit = action.payload;
      const id = padVisit.id;
      if (!id) return state;
      const current = state[VISIT_SCHEDULE_KEYS.CALENDAR][NORMALIZED_KEYS.BY_PAD_VISIT_ID][id];
      if (!current) return state;
      const toggled = !current.automaticRepeat;

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.CALENDAR]: {
          ...state[VISIT_SCHEDULE_KEYS.CALENDAR],
          [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {
            ...state[VISIT_SCHEDULE_KEYS.CALENDAR][NORMALIZED_KEYS.BY_PAD_VISIT_ID],
            [id]: {
              ...current,
              automaticRepeat: toggled,
              dailyAutomaticRepeat: toggled ? false : current.dailyAutomaticRepeat
            }
          }
        }
      };
    }
    case ACTION_TYPES.DAILY_AUTOMATIC_REPETITION: {
      const padVisit = action.payload;
      const id = padVisit.id;
      if (!id) return state;
      const current = state[VISIT_SCHEDULE_KEYS.CALENDAR][NORMALIZED_KEYS.BY_PAD_VISIT_ID][id];
      if (!current) return state;
      const toggled = !current.dailyAutomaticRepeat;

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.CALENDAR]: {
          ...state[VISIT_SCHEDULE_KEYS.CALENDAR],
          [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {
            ...state[VISIT_SCHEDULE_KEYS.CALENDAR][NORMALIZED_KEYS.BY_PAD_VISIT_ID],
            [id]: {
              ...current,
              dailyAutomaticRepeat: toggled,
              automaticRepeat: toggled ? false : current.automaticRepeat
            }
          }
        }
      };
    }
    case ACTION_TYPES.CLEAR_SCHEDULE: {
      const padVisit = action.payload;
      const id = padVisit.id;
      if (!id) return state;
      const current = state[VISIT_SCHEDULE_KEYS.CALENDAR][NORMALIZED_KEYS.BY_PAD_VISIT_ID][id];
      if (!current) return state;

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.CALENDAR]: {
          ...state[VISIT_SCHEDULE_KEYS.CALENDAR],
          [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {
            ...state[VISIT_SCHEDULE_KEYS.CALENDAR][NORMALIZED_KEYS.BY_PAD_VISIT_ID],
            [id]: {
              ...current,
              automaticRepeat: false,
              dailyAutomaticRepeat: false,
              events: []
            }
          }
        }
      };
    }
    case ACTION_TYPES.SET_CAN_STEP_FORWARD: {
      const { padVisitId, canStepFoward, sliceKey } = action.payload;
      if (!padVisitId || !sliceKey) return state;
      const slice = state[sliceKey];
      if (!slice) return state;
      const currentSchedule = slice[NORMALIZED_KEYS.BY_PAD_VISIT_ID][padVisitId];
      if (!currentSchedule) return state;

      return {
        ...state,
        [sliceKey]: {
          ...slice,
          [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {
            ...slice[NORMALIZED_KEYS.BY_PAD_VISIT_ID],
            [padVisitId]: {
              ...currentSchedule,
              canStepFoward: canStepFoward
            }
          }
        }
      };
    }
    case ACTION_TYPES.SET_SHIFT_START_TIME: {
      const { pad } = action.payload;
      if (!pad) return state;

      const padVisits = pad.padVisits || [];
      const padPrescriptions = pad.padPrescriptions || [];

      // Transform using centralized business logic
      const calendarVisitScheduleArray =
        filterAndTransformPadVisitsToCalendarVisitSchedule(padVisits);

      // Transform using centralized business logic
      const calendarVisitScheduleCooperativeArray =
        filterAndTransformPadVisitsToCalendarVisitScheduleCooperative(padVisits);

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.CALENDAR]: {
          ...normalizeCalendarVisitSchedule(calendarVisitScheduleArray),
          status: STATUS.SUCCEEDED,
          error: null
        },
        [VISIT_SCHEDULE_KEYS.CALENDAR_COOPERATIVE]: {
          ...normalizeCalendarVisitSchedule(calendarVisitScheduleCooperativeArray),
          status: STATUS.SUCCEEDED,
          error: null
        },
        [VISIT_SCHEDULE_KEYS.DAILY]: {
          ...state[VISIT_SCHEDULE_KEYS.DAILY],
          status: STATUS.SUCCEEDED,
          error: null
        },
        [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: {
          ...normalizeVisitPrescriptionSchedule(padPrescriptions),
          status: STATUS.SUCCEEDED,
          error: null
        }
      };
    }
    /**
     * FormVisits structure (nested normalization):
     * {
     *   ids: [formCode1, formCode2, ...],
     *   byFormVisitCode: {
     *     [formCode]: { code, professionalId, startDate, workSchedule, period, workShift }
     *   }
     * }
     * This nested structure is used specifically for form data within daily schedules.
     */
    case ACTION_TYPES.DAILY_SCHEDULE_FORM_CHANGE: {
      const { padVisit, dailyScheduleForm } = action.payload;
      const scheduleId = padVisit.id;
      if (!scheduleId) return state;
      const currentSchedule =
        state[VISIT_SCHEDULE_KEYS.DAILY][NORMALIZED_KEYS.BY_PAD_VISIT_ID][scheduleId];
      if (!currentSchedule) return state;
      const formCode = dailyScheduleForm.code;
      if (formCode === undefined) return state;
      const existingForm = currentSchedule.formVisits.byFormVisitCode[formCode];
      if (!existingForm) return state;
      const updatedForm = { ...existingForm, ...dailyScheduleForm };

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.DAILY]: {
          ...state[VISIT_SCHEDULE_KEYS.DAILY],
          [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {
            ...state[VISIT_SCHEDULE_KEYS.DAILY][NORMALIZED_KEYS.BY_PAD_VISIT_ID],
            [scheduleId]: {
              ...currentSchedule,
              formVisits: {
                ...currentSchedule.formVisits,
                byFormVisitCode: {
                  ...currentSchedule.formVisits.byFormVisitCode,
                  [formCode]: updatedForm
                }
              }
            }
          }
        }
      };
    }
    case ACTION_TYPES.DAILY_SCHEDULE_FORM_ADD: {
      const padVisit = action.payload;
      const scheduleId = padVisit.id;
      if (!scheduleId) return state;
      const currentSchedule =
        state[VISIT_SCHEDULE_KEYS.DAILY][NORMALIZED_KEYS.BY_PAD_VISIT_ID][scheduleId];
      if (!currentSchedule) return state;
      const newForm = {
        code: Date.now(),
        professionalId: null,
        startDate: null,
        workSchedule: null,
        period: null,
        workShift: null
      };
      const newFormCode = newForm.code;
      const scheduleIds = state[VISIT_SCHEDULE_KEYS.DAILY][NORMALIZED_KEYS.IDS].includes(scheduleId)
        ? state[VISIT_SCHEDULE_KEYS.DAILY][NORMALIZED_KEYS.IDS]
        : [...state[VISIT_SCHEDULE_KEYS.DAILY][NORMALIZED_KEYS.IDS], scheduleId];

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.DAILY]: {
          [NORMALIZED_KEYS.IDS]: scheduleIds,
          [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {
            ...state[VISIT_SCHEDULE_KEYS.DAILY][NORMALIZED_KEYS.BY_PAD_VISIT_ID],
            [scheduleId]: {
              ...currentSchedule,
              canStepFoward: false,
              formVisits: {
                ids: [...currentSchedule.formVisits.ids, newFormCode],
                byFormVisitCode: {
                  ...currentSchedule.formVisits.byFormVisitCode,
                  [newFormCode]: newForm
                }
              }
            }
          }
        }
      };
    }
    // Ajuste para calculo de frequencia
    // case getAsyncActionTypePending(ACTION_TYPES.UPDATE_SCHEDULE_PRESCRIPTION): {
    //   return {
    //     ...state,
    //     [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: {
    //       ...state[VISIT_SCHEDULE_KEYS.PRESCRIPTION],
    //       status: STATUS.LOADING,
    //       error: null
    //     }
    //   };
    // }
    // case getAsyncActionTypeReject(ACTION_TYPES.UPDATE_SCHEDULE_PRESCRIPTION): {
    //   return {
    //     ...state,
    //     [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: {
    //       ...state[VISIT_SCHEDULE_KEYS.PRESCRIPTION],
    //       status: STATUS.FAILED,
    //       error: action.error
    //     }
    //   };
    // }
    case ACTION_TYPES.UPDATE_SCHEDULE_PRESCRIPTION: {
      const { calendarDailySchedules, calendarVisitSchedule, visitPrescriptionSchedules } =
        computeUpdateSchedulePrescriptionState(state, action.payload);

      const { calendarVisitScheduleCooperative, visitPrescriptionSchedulesCooperative } =
        computeUpdateSchedulePrescriptionCooperativeState(state, action.payload);

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.CALENDAR]: { ...calendarVisitSchedule },
        [VISIT_SCHEDULE_KEYS.DAILY]: { ...calendarDailySchedules },
        [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: { ...visitPrescriptionSchedules },

        [VISIT_SCHEDULE_KEYS.CALENDAR_COOPERATIVE]: { ...calendarVisitScheduleCooperative },
        [VISIT_SCHEDULE_KEYS.PRESCRIPTION_COOPERATIVE]: { ...visitPrescriptionSchedulesCooperative }
      };
    }
    // Ajuste para calculo de frequencia
    // case ACTION_TYPES.UPDATE_SCHEDULE_PRESCRIPTION: {
    //   const { calendarDailySchedules, calendarVisitSchedule, visitPrescriptionSchedules } =
    //     computeUpdateSchedulePrescriptionState(state, action.payload);
    //
    //   const { calendarVisitScheduleCooperative, visitPrescriptionSchedulesCooperative } =
    //     computeUpdateSchedulePrescriptionCooperativeState(state, action.payload);
    //
    //   return {
    //     ...state,
    //     [VISIT_SCHEDULE_KEYS.CALENDAR]: { ...calendarVisitSchedule },
    //     [VISIT_SCHEDULE_KEYS.DAILY]: { ...calendarDailySchedules },
    //     [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: {
    //       ...visitPrescriptionSchedules,
    //       status: STATUS.SUCCEEDED,
    //       error: null
    //     },
    //
    //     [VISIT_SCHEDULE_KEYS.CALENDAR_COOPERATIVE]: { ...calendarVisitScheduleCooperative },
    //     [VISIT_SCHEDULE_KEYS.PRESCRIPTION_COOPERATIVE]: { ...visitPrescriptionSchedulesCooperative }
    //   };
    // }
    case getAsyncActionTypeFulfiled(ACTION_TYPES.FETCH_PAD_DATA): {
      const pad = action.payload;
      const padVisits = pad.padVisits || [];
      const prescriptions = pad.padPrescriptions || [];

      // Transform using centralized business logic
      const calendarVisitScheduleArray =
        filterAndTransformPadVisitsToCalendarVisitSchedule(padVisits);

      // Transform using centralized business logic
      const calendarVisitScheduleCooperativeArray =
        filterAndTransformPadVisitsToCalendarVisitScheduleCooperative(padVisits);

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.CALENDAR]: {
          ...normalizeCalendarVisitSchedule(calendarVisitScheduleArray),
          status: STATUS.SUCCEEDED,
          error: null
        },
        [VISIT_SCHEDULE_KEYS.CALENDAR_COOPERATIVE]: {
          ...normalizeCalendarVisitSchedule(calendarVisitScheduleCooperativeArray),
          status: STATUS.SUCCEEDED,
          error: null
        },
        [VISIT_SCHEDULE_KEYS.DAILY]: {
          ...state[VISIT_SCHEDULE_KEYS.DAILY],
          status: STATUS.SUCCEEDED,
          error: null
        },
        [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: {
          ...normalizeVisitPrescriptionSchedule(prescriptions),
          status: STATUS.SUCCEEDED,
          error: null
        }
      };
    }
    case getAsyncActionTypeFulfiled(ACTION_TYPES.FETCH_PAD_VISIT_DATA): {
      const padVisit = action.payload;

      // Transform using centralized business logic
      const calendarVisitScheduleObj =
        transformPadVisitToCalendarVisitScheduleCooperative(padVisit);

      // Transform using centralized business logic
      const visitPrescriptionScheduleObj =
        transformPadVisitToVisitPrescriptionScheduleCooperative(padVisit);

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.CALENDAR]: {
          ...normalizeCalendarVisitScheduleCooperative(calendarVisitScheduleObj),
          status: STATUS.SUCCEEDED,
          error: null
        },
        [VISIT_SCHEDULE_KEYS.DAILY]: {
          ...state[VISIT_SCHEDULE_KEYS.DAILY],
          status: STATUS.SUCCEEDED,
          error: null
        },
        [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: {
          ...normalizeVisitPrescriptionScheduleCooperative(visitPrescriptionScheduleObj),
          status: STATUS.SUCCEEDED,
          error: null
        }
      };
    }
    case getAsyncActionTypePending(ACTION_TYPES.FETCH_PAD_DATA): {
      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.CALENDAR]: {
          ...state[VISIT_SCHEDULE_KEYS.CALENDAR],
          status: STATUS.LOADING,
          error: null
        },
        [VISIT_SCHEDULE_KEYS.DAILY]: {
          ...state[VISIT_SCHEDULE_KEYS.DAILY],
          status: STATUS.LOADING,
          error: null
        },
        [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: {
          ...state[VISIT_SCHEDULE_KEYS.PRESCRIPTION],
          status: STATUS.LOADING,
          error: null
        }
      };
    }

    case getAsyncActionTypeReject(ACTION_TYPES.FETCH_PAD_DATA): {
      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.CALENDAR]: {
          ...state[VISIT_SCHEDULE_KEYS.CALENDAR],
          status: STATUS.FAILED,
          error: action.error
        },
        [VISIT_SCHEDULE_KEYS.DAILY]: {
          ...state[VISIT_SCHEDULE_KEYS.DAILY],
          status: STATUS.FAILED,
          error: action.error
        },
        [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: {
          ...state[VISIT_SCHEDULE_KEYS.PRESCRIPTION],
          status: STATUS.FAILED,
          error: action.error
        }
      };
    }

    case ACTION_TYPES.RESET: {
      return initialState();
    }
    default:
      return state;
  }
};

export default reducer;
